import React from "react";
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "types/store";
import styles from "./style.module.css";
import { Button } from "aible";
import { LineInput } from "components/Inputs/LineInput";
import TextInput from "components/Inputs/TextInput";
import LabeledToggle from "components/Inputs/Toggle/Toggle";
import { toast } from "react-toastify";
import BlueprintTile from "components/Tiles/BlueprintTile.js";
import blueprintImport from "images/export.svg";
import DocumentSelector from "components/LanguageModelArtifact/DocumentSelector";
import * as blueprintReducer from "containers/Blueprint/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/aible/Dialog/Dialog";
import { getModelOptions } from "containers/ChatGPT/reducer";
import ModelSelector from "components/ChatGPT/ModelSelector";
import * as RelationUtils from "utils/relation";
import DatasetSelector from "components/Dataset/DatasetSelector";
import { copyAndUpdate, getDataset, getDatasetsBySource } from "@/containers/Home/reducer";
import { isEnabled } from "utils/featureFlags";
import { importFromFile } from "@/utils/fileExportImport";
const BlueprintDatasetSelector = ({ referenceSource, datasetId, updateDatasetId }) => {
    const possibleDatasets = useSelector((state) => getDatasetsBySource(state, referenceSource));
    const possibleDatasetIdSet = new Set(possibleDatasets.map((d) => d.id));
    return (React.createElement(DatasetSelector, { datasetId: datasetId < 0 ? undefined : datasetId, onChange: updateDatasetId, isStyled: false, isDynamic: false, menuPlacement: "bottom", forceDatasetSelection: true, hideJustChat: true, allowUpload: true, allowAllDatasets: true, filter: (d) => possibleDatasetIdSet.has(d.id) }));
};
const BlueprintCreate = ({ showModal, ...retProps } = {}) => {
    const dispatch = useDispatch();
    const [bpShowModal, updateBpShowModal] = useState(showModal);
    const [blueprint, updateBlueprint] = useState(undefined);
    const handleSaveBlueprint = (bp, useCase) => {
        dispatch(blueprintReducer.createBlueprintFromBlueprint(bp, useCase));
        updateBlueprint(undefined);
    };
    const handleUpdateShowModal = (val) => {
        updateBpShowModal(val);
        if (!val) {
            updateBlueprint(undefined);
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(BlueprintTile, { noPermissionsText: "You don't have permission to create a agent template in this folder.", isCreate: true, onClick: () => {
                navigator.clipboard.readText().then((v) => {
                    if (v) {
                        try {
                            const parsedBp = JSON.parse(v);
                            parsedBp.wizard.chatGPT.recommendedDatasetIds = [];
                            parsedBp.wizard.chatGPT.recommendedDocumentsetIds = [];
                            updateBlueprint(parsedBp);
                        }
                        catch (e) {
                            console.log(e);
                        }
                    }
                });
                updateBpShowModal(true);
            }, image: blueprintImport, onClickLabel: "Import Agent Template", ...retProps }),
        React.createElement(CreateBlueprintModal, { defaultBlueprint: blueprint, saveBlueprint: handleSaveBlueprint, showModal: bpShowModal, updateShowModal: handleUpdateShowModal })));
};
export const ImportBlueprint = ({ children, style, label }) => {
    const dispatch = useDispatch();
    const [showModal, updateShowModal] = useState(false);
    const [blueprint, updateBlueprint] = useState(undefined);
    const handleSaveBlueprint = async (bp, useCase, isChatAppUseCase) => {
        if (isChatAppUseCase) {
            const newBp = await dispatch(blueprintReducer.createBlueprintFromBlueprint(bp, useCase, undefined, undefined, undefined, undefined, true));
            return newBp;
        }
        const isChatApp = bp?.wizard?.isChatApp;
        const newBp = await dispatch(blueprintReducer.createBlueprintFromBlueprint(bp, useCase, undefined, isChatApp));
        updateBlueprint(undefined);
        return newBp;
    };
    const handleUpdateShowModal = (val) => {
        updateShowModal(val);
        if (!val) {
            updateBlueprint(undefined);
        }
    };
    const handleClick = () => {
        navigator.clipboard.readText().then((v) => {
            if (v) {
                try {
                    const parsedBp = JSON.parse(v);
                    if (parsedBp?.wizard?.chatGPT?.recommendedDatasetIds) {
                        parsedBp.wizard.chatGPT.recommendedDatasetIds = [];
                    }
                    if (parsedBp?.wizard?.chatGPT?.recommendedDocumentsetIds) {
                        parsedBp.wizard.chatGPT.recommendedDocumentsetIds = [];
                    }
                    updateBlueprint(parsedBp);
                }
                catch (e) {
                    console.log(e);
                }
            }
        });
        handleUpdateShowModal(true);
    };
    return (React.createElement(React.Fragment, null,
        children ? (React.createElement("div", { style: style, onClick: handleClick }, children)) : (React.createElement(Button, { onClick: handleClick, type: "secondary", style: style, active: showModal }, label ?? "Import Agent Template")),
        React.createElement(CreateBlueprintModal, { defaultBlueprint: blueprint, saveBlueprint: handleSaveBlueprint, showModal: showModal, updateShowModal: handleUpdateShowModal })));
};
export const CreateBlueprintModal = ({ defaultBlueprint, updateShowModal, showModal, saveBlueprint }) => {
    const dispatch = useDispatch();
    const [importFileName, setImportFileName] = useState();
    const [blueprint, updateBlueprint] = useState(defaultBlueprint);
    const [isSaving, setIsSaving] = useState(false);
    const fileInputRef = React.useRef(null);
    const folderId = useSelector((state) => foldersReducer.getSelectedFolder(state));
    const modelOptions = useSelector((state) => getModelOptions(state, undefined, true, isEnabled("provisioned-models-filter-interns")));
    const getBlueprint = (bpId) => dispatch((dispatch, getState) => {
        return blueprintReducer.getBlueprint(getState(), bpId);
    });
    const validateBlueprintValues = (bp, folderId) => {
        if (!bp) {
            return bp;
        }
        const bpModel = bp?.wizard?.modelId;
        if (bpModel && !modelOptions?.find((o) => o.value === bpModel)) {
            return {
                ...bp,
                wizard: {
                    ...bp.wizard,
                    modelId: "INVALID_MODELID"
                }
            };
        }
        if (folderId && blueprint?.folder_id !== folderId && blueprint?.wizard?.chatGPT) {
            blueprint.wizard.chatGPT.recommendedDatasetIds = [];
            blueprint.wizard.chatGPT.recommendedDocumentsetIds = [];
        }
        return bp;
    };
    const bpChatType = useSelector((state) => {
        const useCaseId = blueprint?.wizard?.steps?.[0]?.chatBlueprintId;
        if (!useCaseId) {
            return;
        }
        const useCase = blueprint?.stepsBlueprints?.[useCaseId] ?? blueprintReducer.getBlueprint(state, useCaseId);
        return useCase?.wizard?.chatGPT?.chatType;
    });
    const isChatApp = blueprint?.wizard?.isChatApp;
    const bpDatasetId = blueprint?.wizard?.steps?.[0]?.datasetId;
    const bpDocumentId = blueprint?.wizard?.steps?.[0]?.artifactId;
    const showDatasetSelector = bpChatType === "AIBLE" ||
        bpChatType === "ANALYTICS" ||
        bpChatType === "SQL" ||
        bpChatType === "WHATS_CHANGED" ||
        bpChatType === "KPI_ANALYSIS" ||
        bpChatType === "OPTIMIZE";
    const showDocumentSelector = bpChatType === "DOCUMENT";
    useEffect(() => {
        const cleanedBlueprint = validateBlueprintValues(defaultBlueprint, folderId);
        updateBlueprint(cleanedBlueprint);
    }, [defaultBlueprint]);
    const blueprintModelId = blueprint?.wizard?.modelId ?? blueprint?.wizard?.chatGPT?.modelId;
    const blueprintSteps = blueprint?.wizard?.steps?.["0"];
    const blueprintDatasetSettings = blueprintSteps?.datasetSettings === undefined
        ? undefined
        : JSON.parse(JSON.stringify(blueprintSteps?.datasetSettings));
    const blueprintDatasetRelation = blueprintDatasetSettings?.transformations.plan?.relation === undefined
        ? undefined
        : blueprintDatasetSettings?.transformations.plan?.relation;
    const blueprintDatasetRelationSources = blueprintDatasetRelation === undefined ? [] : RelationUtils.getSources(blueprintDatasetRelation);
    const inputDataSourcesByUrl = blueprintDatasetRelationSources.reduce((o, s) => {
        o[s.url] = s;
        return o;
    }, {});
    const defaultSourceDatasetByUrl = Object.keys(inputDataSourcesByUrl).reduce((o, k) => {
        o[k] = -1;
        return o;
    }, {});
    const [sourceDatasetByUrl, updateSourceDatasetsByUrl] = useState();
    const finalSourceDatasetByUrl = sourceDatasetByUrl ?? defaultSourceDatasetByUrl;
    const hasMissingDataSources = Object.values(finalSourceDatasetByUrl).filter((x) => x == -1).length > 0;
    const hasSourceDatasetChecks = Object.keys(defaultSourceDatasetByUrl).length > 0;
    const [isSelectFromAllData, setIsSelectFromAllData] = useState(!hasSourceDatasetChecks);
    useEffect(() => {
        setIsSelectFromAllData(!hasSourceDatasetChecks);
    }, [hasSourceDatasetChecks]);
    const sourceDatasetsById = useSelector((state) => Object.values(finalSourceDatasetByUrl)
        .filter((id) => id > 0)
        .map((id) => getDataset(state, id))
        .filter((d) => d !== undefined)
        .reduce((o, d) => {
        o[d.id] = d;
        return o;
    }, {}));
    const handleSaveBlueprint = async () => {
        if (!isChatApp) {
            return saveBlueprint(blueprint, true);
        }
        // If the blueprint is a chatapp, save the use cases first and link them to the project
        // Blueprint lookup for blueprints we need to save
        const useCaseObj = blueprint.stepsBlueprints ?? {};
        // New IDs of blueprints we already saved
        const newBpIdLookup = {};
        // If the blueprints already exist in this folder, just use those blueprints
        for (const useCaseBpId in useCaseObj) {
            const useCaseBp = getBlueprint(useCaseBpId);
            if (useCaseBp?.id && !isNaN(Number(useCaseBpId))) {
                newBpIdLookup[useCaseBpId] = Number(useCaseBpId);
            }
        }
        // Create a new blueprint that doesn't have the extra blueprints to manipulate and save
        const cleanedBlueprint = {
            ...blueprint
        };
        delete cleanedBlueprint.stepsBlueprints;
        const retBlueprint = JSON.parse(JSON.stringify(cleanedBlueprint));
        // John's code to import datasets
        if (blueprintDatasetSettings) {
            const firstSource = blueprintDatasetRelationSources[0];
            if (blueprintDatasetRelation !== undefined && firstSource !== undefined) {
                const finalDataSources = RelationUtils.getSources(blueprintDatasetRelation);
                for (const source of finalDataSources) {
                    const selectedDataset = sourceDatasetsById[finalSourceDatasetByUrl[source.url]];
                    if (selectedDataset === undefined) {
                        continue;
                    }
                    // mutate this object so that the relation is updated
                    source.url = selectedDataset.source.url;
                    source.content = selectedDataset.source.content;
                }
                const sourceDatasetId = finalSourceDatasetByUrl[firstSource.url];
                const dataset = await dispatch(copyAndUpdate(sourceDatasetId, blueprintDatasetSettings));
                if (dataset !== undefined) {
                    // Robert's changes: Modify all mentions of the dataset to the new datasetId
                    Object.keys(retBlueprint.wizard.steps ?? {}).forEach((sKey) => {
                        retBlueprint.wizard.steps[sKey].datasetId = dataset.id;
                    });
                    retBlueprint.wizard.allowedAppDatasets = [dataset.id];
                    retBlueprint.wizard.datasetId = dataset.id;
                }
            }
        }
        // Look in the steps
        for (const sKey in retBlueprint.wizard.steps ?? {}) {
            const cBpId = retBlueprint.wizard.steps[sKey].chatBlueprintId;
            // Only save the blueprint if it is not a hardcoded blueprint and not already saved
            if (useCaseObj[cBpId] && !newBpIdLookup[cBpId]) {
                const newBp = await saveBlueprint(useCaseObj[cBpId], true, true);
                newBpIdLookup[cBpId] = Number(newBp?.id);
            }
            retBlueprint.wizard.steps[sKey].chatBlueprintId =
                newBpIdLookup[cBpId] ?? retBlueprint.wizard.steps[sKey].chatBlueprintId;
        }
        // Look in the allowedAppUseCases
        if (retBlueprint.wizard.allowedAppUseCases?.length) {
            for (const cBpId of retBlueprint.wizard.allowedAppUseCases ?? []) {
                if (useCaseObj[cBpId] && !newBpIdLookup[cBpId]) {
                    const newBp = await saveBlueprint(useCaseObj[cBpId], true, true);
                    newBpIdLookup[cBpId] = Number(newBp?.id);
                }
            }
            retBlueprint.wizard.allowedAppUseCases = retBlueprint.wizard.allowedAppUseCases.map((cBpId) => {
                return newBpIdLookup[cBpId] ?? cBpId;
            });
        }
        const newBp = await saveBlueprint(retBlueprint, true);
        return newBp;
    };
    const isInvalidModelId = blueprint?.wizard?.modelId === "INVALID_MODELID";
    const isInvalidImport = !blueprint || typeof blueprint === "string";
    const disableSave = isInvalidImport || (isChatApp && isInvalidModelId);
    return (React.createElement(Dialog, { open: showModal, onOpenChange: () => updateShowModal(false) },
        React.createElement(DialogContent, null,
            React.createElement(DialogHeader, null,
                React.createElement(DialogTitle, null, "Import Agent Template")),
            React.createElement("div", null,
                React.createElement("div", { className: styles.setting },
                    React.createElement("div", { className: styles.chatSettingsItemInput },
                        React.createElement(Button, { onClick: () => {
                                fileInputRef?.current?.click();
                            } }, "Upload Exported Template"),
                        importFileName ? React.createElement("div", { style: { marginLeft: "10px" } }, importFileName) : null),
                    React.createElement("input", { type: "file", ref: fileInputRef, onChange: async (event) => {
                            const file = event?.target?.files?.[0];
                            if (file) {
                                try {
                                    setImportFileName(file.name);
                                    const blueprint = await importFromFile(file);
                                    const cleanedBp = validateBlueprintValues(blueprint, folderId);
                                    updateBlueprint(cleanedBp);
                                }
                                catch (e) {
                                    console.error(e);
                                    setImportFileName(undefined);
                                    updateBlueprint(undefined);
                                }
                            }
                            else {
                                event.target.value = "";
                            }
                        }, accept: ".aible", style: { display: "none" } })),
                React.createElement("div", { className: styles.orSeparator }, "OR"),
                React.createElement("div", { className: styles.setting },
                    React.createElement("div", { className: styles.settingLabel }, "Agent Template JSON"),
                    React.createElement(TextInput, { style: { height: "75px", color: !isInvalidImport ? "#A4A6B7" : undefined }, value: blueprint ? JSON.stringify(blueprint) : "", onChange: (val) => {
                            if (val) {
                                try {
                                    const retBp = JSON.parse(val);
                                    const cleanedBp = validateBlueprintValues(retBp, folderId);
                                    updateBlueprint(cleanedBp);
                                }
                                catch (e) {
                                    console.log(e);
                                    toast(`Invalid Agent Template JSON. Please try again.`, {
                                        type: toast.TYPE.ERROR,
                                        position: toast.POSITION.TOP_CENTER,
                                        autoClose: 3500
                                    });
                                }
                            }
                            else {
                                updateBlueprint(undefined);
                            }
                        }, placeholder: "Paste Agent Template JSON here." }),
                    "Name",
                    React.createElement(LineInput, { isDisabled: isInvalidImport, value: blueprint?.name ?? "", onChange: (val) => updateBlueprint({ ...blueprint, name: val }) }),
                    React.createElement("div", { className: styles.settingLabel }, "Description"),
                    React.createElement(TextInput, { isDisabled: isInvalidImport, value: blueprint?.wizard?.description ?? "", onChange: (val) => {
                            return updateBlueprint({
                                ...blueprint,
                                wizard: {
                                    ...blueprint?.wizard,
                                    description: val
                                }
                            });
                        } }),
                    blueprintModelId !== undefined && (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: styles.settingLabel }, "Confirm the AI Summarization Model"),
                        React.createElement("div", { className: styles.wizardLightText }, "This model will understand context and summarize the answer"),
                        isInvalidModelId ? (React.createElement("div", { className: styles.wizardLightText, style: { color: "#c03d5a" } }, "The default model for this agent is not available, please select a new model")) : null,
                        React.createElement(ModelSelector, { isCreatable: true, menuPlacement: "top", forceModelId: true, modelId: isInvalidModelId ? undefined : blueprintModelId, updateModelId: (val) => {
                                let retBp = blueprint;
                                if (isChatApp) {
                                    retBp = {
                                        ...blueprint,
                                        wizard: {
                                            ...blueprint.wizard,
                                            modelId: val
                                        }
                                    };
                                    if (retBp?.wizard?.secondaryModelId) {
                                        retBp.wizard.secondaryModelId = val;
                                    }
                                }
                                else if (blueprint?.wizard?.chatGPT?.modelId !== undefined) {
                                    retBp = {
                                        ...blueprint,
                                        wizard: {
                                            ...blueprint?.wizard,
                                            chatGPT: {
                                                ...blueprint?.wizard?.chatGPT,
                                                modelId: val
                                            }
                                        }
                                    };
                                }
                                return updateBlueprint(retBp);
                            } }))),
                    showDatasetSelector ? (React.createElement(React.Fragment, null,
                        React.createElement("div", { className: styles.settingLabel }, "Confirm Agent Data Source"),
                        React.createElement("div", { className: styles.wizardLightText }, "This data will be used as context for answers"),
                        hasSourceDatasetChecks && (React.createElement("div", { className: styles.settingLabel },
                            React.createElement("span", { style: { marginRight: "8px" } }, "Limit selection to auto-detected compatible datasets"),
                            React.createElement(LabeledToggle, { checked: !isSelectFromAllData, onChange: () => setIsSelectFromAllData(!isSelectFromAllData), isAibleColors: true }))),
                        !isSelectFromAllData && hasSourceDatasetChecks ? (Object.entries(finalSourceDatasetByUrl).map(([url, datasetId]) => (React.createElement(BlueprintDatasetSelector, { key: url, referenceSource: inputDataSourcesByUrl[url], datasetId: datasetId, updateDatasetId: (value) => updateSourceDatasetsByUrl({
                                ...finalSourceDatasetByUrl,
                                [url]: value === undefined ? -1 : value
                            }) })))) : (React.createElement(DatasetSelector, { datasetId: bpDatasetId, onChange: (val) => {
                                const retBp = {
                                    ...blueprint,
                                    wizard: {
                                        ...blueprint.wizard
                                    }
                                };
                                Object.keys(blueprint.wizard.steps ?? {}).forEach((sKey) => {
                                    retBp.wizard.steps[sKey].datasetId = val;
                                });
                                retBp.wizard.allowedAppDatasets = [val];
                                retBp.wizard.datasetId = val;
                                return updateBlueprint(retBp);
                            }, menuPlacement: "top", allowAllDatasets: bpChatType === "SQL", filter: bpChatType === "WHATS_CHANGED" ? "date" : undefined })))) : null),
                showDocumentSelector && (React.createElement(React.Fragment, null,
                    React.createElement("div", { className: styles.settingLabel }, "Confirm Agent Document Sets"),
                    React.createElement("div", { className: styles.wizardLightText }, "This data will be used as context for answers"),
                    React.createElement(DocumentSelector, { artifactId: bpDocumentId, onChange: (val) => {
                            const valId = val?.id;
                            const retBp = {
                                ...blueprint,
                                wizard: {
                                    ...blueprint.wizard
                                }
                            };
                            Object.keys(blueprint.wizard.steps ?? {}).forEach((sKey) => {
                                retBp.wizard.steps[sKey].artifactId = valId;
                            });
                            retBp.wizard.allowedAppDocuments = [valId];
                            retBp.wizard.artifactId = valId;
                            return updateBlueprint(retBp);
                        }, menuPlacement: "top" })))),
            React.createElement(DialogFooter, null,
                React.createElement(Button, { type: "secondary", onClick: () => updateShowModal(false), style: { marginRight: "auto" } }, "Close"),
                React.createElement(Button, { type: "primary", onClick: async () => {
                        setIsSaving(true);
                        await handleSaveBlueprint();
                        setIsSaving(false);
                        updateShowModal(false);
                        updateBlueprint(undefined);
                    }, isLoading: isSaving, disabled: disableSave }, "Import")))));
};
export default BlueprintCreate;
